'use client';

import { getTodayLoginGiftStatusAction } from '@/actions/credits/get-today-login-gift-status';
import { AuthSessionHintBoundary } from '@/components/auth/auth-session-hint-boundary';
import { LOGIN_GIFT_NOTICE_DURATION_MS } from '@/credits/login-gift/reward-notice';
import { requestCreditsRefresh } from '@/hooks/credits/credits-refresh';
import { useAuthSessionObserver } from '@/lib/auth/auth-session-observer';
import { setBrowserTimeZoneCookie } from '@/lib/auth/browser-time-zone-cookie';
import { formatBrowserDateTime } from '@/lib/format/browser-date-time';
import { isHomePathname } from '@/lib/routing/is-home-pathname';
import { toast } from '@/lib/ui/toast';
import { useTranslations } from 'next-intl';
import { useEffect, useRef } from 'react';

const LOGIN_GIFT_LAST_SHOWN_TX_STORAGE_KEY = 'login-gift:last-shown-tx';
const REGISTER_GIFT_LAST_SHOWN_TX_STORAGE_KEY = 'register-gift:last-shown-tx';

type GiftKind = 'login' | 'register';

function runRewardCheckWhenPageSettles(callback: () => void) {
  if (typeof window === 'undefined') {
    return () => {};
  }

  let cancelled = false;
  let idleHandle: number | null = null;
  const idleWindow = window as Window & {
    requestIdleCallback?: (cb: () => void) => number;
    cancelIdleCallback?: (id: number) => void;
  };

  const run = () => {
    if (cancelled) return;
    if (typeof idleWindow.requestIdleCallback === 'function') {
      idleHandle = idleWindow.requestIdleCallback(() => {
        if (!cancelled) callback();
      });
      return;
    }

    idleHandle = window.setTimeout(() => {
      if (!cancelled) callback();
    }, 120);
  };

  if (document.readyState === 'complete') {
    run();
    return () => {
      cancelled = true;
      if (idleHandle === null) return;
      if (typeof idleWindow.cancelIdleCallback === 'function') {
        idleWindow.cancelIdleCallback(idleHandle);
        return;
      }
      window.clearTimeout(idleHandle);
    };
  }

  const handleLoad = () => run();
  window.addEventListener('load', handleLoad, { once: true });
  return () => {
    cancelled = true;
    window.removeEventListener('load', handleLoad);
    if (idleHandle === null) return;
    if (typeof idleWindow.cancelIdleCallback === 'function') {
      idleWindow.cancelIdleCallback(idleHandle);
      return;
    }
    window.clearTimeout(idleHandle);
  };
}

export function GiftRewardToastHost() {
  return (
    <AuthSessionHintBoundary
      debugName="gift-reward-toast-host"
      publishToObserver
      revalidateOnMount
    >
      {() => <GiftRewardToastObserver />}
    </AuthSessionHintBoundary>
  );
}

function GiftRewardToastObserver() {
  const { currentUserId, status } = useAuthSessionObserver();

  if (
    status === 'idle' ||
    status === 'loading' ||
    status === 'unauthenticated'
  ) {
    return null;
  }

  return <GiftRewardToastContent currentUserId={currentUserId} />;
}

function GiftRewardToastContent({
  currentUserId,
}: {
  currentUserId: string | null;
}) {
  const t = useTranslations('Common.toasts.gifts');
  const userId = currentUserId;
  const previousUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    const previousUserId = previousUserIdRef.current;
    previousUserIdRef.current = userId;

    if (!userId || previousUserId === userId) {
      return;
    }

    let cancelled = false;

    const checkTodayLoginGift = async () => {
      try {
        setBrowserTimeZoneCookie();
        const result = await getTodayLoginGiftStatusAction();
        const data = result?.data;
        if (cancelled || !data || data.success !== true) return;
        let shouldRefreshCredits = false;

        const showGiftToast = (params: {
          kind: GiftKind;
          credits: number;
          transactionId: string;
          nextResetAt: string | null;
          storageKeyPrefix: string;
        }) => {
          if (params.credits <= 0) return;

          const storageKey = `${params.storageKeyPrefix}:${userId}`;
          const lastShownTxId = window.localStorage.getItem(storageKey);
          if (lastShownTxId === params.transactionId) return;

          window.localStorage.setItem(storageKey, params.transactionId);
          let message: string;
          if (params.kind === 'login') {
            if (!params.nextResetAt) return;
            message = t('login', {
              credits: params.credits,
              resetTime: formatBrowserDateTime(params.nextResetAt),
            });
          } else {
            message = t('register', { credits: params.credits });
          }
          toast.success(message, {
            id: `gift:${params.kind}:${params.transactionId}`,
            duration: LOGIN_GIFT_NOTICE_DURATION_MS,
          });
        };

        for (const notice of data.rewardNotices) {
          shouldRefreshCredits = true;
          showGiftToast({
            kind: notice.kind,
            credits: notice.credits,
            transactionId: notice.transactionId,
            nextResetAt: notice.nextResetAt,
            storageKeyPrefix:
              notice.kind === 'register'
                ? REGISTER_GIFT_LAST_SHOWN_TX_STORAGE_KEY
                : LOGIN_GIFT_LAST_SHOWN_TX_STORAGE_KEY,
          });
        }

        if (shouldRefreshCredits) {
          requestCreditsRefresh();
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('failed to check global login gift status', error);
        }
      }
    };

    if (
      typeof window !== 'undefined' &&
      isHomePathname(window.location.pathname)
    ) {
      return runRewardCheckWhenPageSettles(() => {
        void checkTodayLoginGift();
      });
    }

    void checkTodayLoginGift();

    return () => {
      cancelled = true;
    };
  }, [t, userId]);

  return null;
}
